import React, { useRef, useEffect } from 'react';
import { ChevronDown, Check, Plus, ExternalLink } from 'lucide-react';
import { useVenues } from '../../hooks/useVenues';
import { clsx } from 'clsx';
import { CreateVenueModal } from './CreateVenueModal';
import { getPlatformMenuUrl } from '../../lib/utils';
import { buildMenuPath, resolveDefaultLocale } from '../../lib/locales';

export const VenueSwitcher: React.FC = () => {
  const { venues, activeVenue, setActiveVenue } = useVenues();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerOutside = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('pointerdown', handlePointerOutside);
    }

    return () => {
      document.removeEventListener('pointerdown', handlePointerOutside);
    };
  }, [isOpen]);

  if (venues.length === 0) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex min-h-12 items-center gap-3 px-4 py-2 bg-brand-surface/50 hover:bg-white/5 border border-white/10 rounded-xl transition-all group touch-manipulation"
        aria-expanded={isOpen}
      >
        <div className="w-8 h-8 rounded-lg bg-brand-primary/20 flex items-center justify-center text-brand-primary font-bold group-hover:scale-105 transition-transform">
          {activeVenue?.name.charAt(0).toUpperCase() || 'V'}
        </div>
        <div className="text-left hidden sm:block min-w-[120px]">
          <p className="text-sm font-bold text-white leading-none mb-1 truncate">{activeVenue?.name || 'Loading...'}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Switch Location</p>
        </div>
        <ChevronDown size={16} className={clsx("text-gray-400 transition-transform duration-300", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-brand-surface/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-2 space-y-1">
              <p className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">Your Venues</p>
              
              {venues.map((venue) => (
                <div key={venue.id} className="relative group">
                  <button
                    onClick={() => {
                      setActiveVenue(venue);
                      setIsOpen(false);
                    }}
                    className={clsx(
                      "w-full min-h-14 flex items-center justify-between p-3 rounded-xl transition-all text-left group/btn relative backdrop-blur-sm pr-20 touch-manipulation",
                      activeVenue?.id === venue.id ? "bg-brand-primary/10 text-brand-primary" : "hover:bg-white/5 text-gray-400 hover:text-white"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={clsx(
                        "w-10 h-10 rounded-lg flex items-center justify-center font-bold",
                        activeVenue?.id === venue.id ? "bg-brand-primary/20" : "bg-white/5"
                      )}>
                        {venue.name.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <span className="font-bold block text-sm truncate">{venue.name}</span>
                        <span className="text-[10px] opacity-50 block uppercase tracking-wider">
                          {activeVenue?.id === venue.id ? 'Active Venue' : 'Switch To'}
                        </span>
                      </div>
                    </div>
                    {activeVenue?.id === venue.id && (
                      <div className="w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center text-white absolute right-14">
                        <Check size={12} strokeWidth={3} />
                      </div>
                    )}
                  </button>
                  
                  {/* External Link Shortcut */}
                  <a 
                    href={`${getPlatformMenuUrl()}${buildMenuPath(venue.slug, resolveDefaultLocale(venue.default_locale, venue.enabled_locales))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/5 border border-white/5 hover:bg-brand-primary text-gray-400 hover:text-white rounded-xl transition-all touch-manipulation shadow-sm"
                    title={`View ${venue.name} Live Menu`}
                    aria-label={`View ${venue.name} live menu`}
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
              ))}
              
              <div className="h-px bg-white/5 my-2 mx-2" />
              
              <button 
                onClick={() => { setIsModalOpen(true); setIsOpen(false); }}
                className="w-full min-h-14 flex items-center gap-3 p-3 rounded-xl text-brand-primary hover:bg-brand-primary/10 transition-all font-bold text-sm touch-manipulation"
              >
                <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center">
                  <Plus size={18} />
                </div>
                Create New Venue
              </button>
            </div>
          </div>
      )}

      <CreateVenueModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};
